import React, { useState, useEffect, useRef } from 'react';

import { Autocomplete, AutocompleteProps, AutocompleteItem } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import useAutoCompleteAppointmentCoachRoles from 'hooks/appointmentCoachRole/useAutoCompleteAppointmentCoachRoles';

import SelectorButton from './SelectorButton';

export interface AppointmentCoachRoleAutoCompleteValue extends AutocompleteItem {
  appointmentCoachRoleId: string;
  label: string;
}

interface IAppointmentCoachRoleAutoCompleteProps
  extends Omit<
    AutocompleteProps,
    'data' | 'value' | 'onChange' | 'filter' | 'onItemSubmit' | 'rightSection'
  > {
  value: AppointmentCoachRoleAutoCompleteValue;
  onChange: (item: AppointmentCoachRoleAutoCompleteValue | null) => void;
}

const AppointmentCoachRoleAutoComplete = ({
  value,
  onChange,
  ...autocompleteProps
}: IAppointmentCoachRoleAutoCompleteProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [options, setOptions] = useState<AutocompleteItem[]>([]);
  const [search, setSearch] = useState(value.label);
  const [debouncedSearch] = useDebouncedValue(search, 250);

  const { data, isSuccess, isLoading } = useAutoCompleteAppointmentCoachRoles({
    search: debouncedSearch,
  });

  const handleSearchChange = (query: string): void => {
    setSearch(query);
    onChange(null);
  };
  const handleItemSubmit = (item: AppointmentCoachRoleAutoCompleteValue): void => {
    setSearch('');
    onChange(item);
  };
  const handleClearSearch = (): void => {
    setSearch('');
  };

  const RightSection = (
    <SelectorButton
      isLoading={isLoading}
      hasText={!!(value.label || search)}
      handleClick={() => handleSearchChange('')}
      handleFocus={() => inputRef?.current?.focus()}
    />
  );

  useEffect(() => {
    if (isSuccess && data) {
      setOptions(
        data.data.map(acr => ({
          ...acr,
          value: acr.appointmentCoachRoleId,
          label: acr.name,
        })),
      );
    }
  }, [isSuccess, data]);

  return (
    <Autocomplete
      ref={inputRef}
      rightSection={RightSection}
      value={value.label || search}
      onChange={handleSearchChange}
      onItemSubmit={handleItemSubmit}
      onDropdownClose={handleClearSearch}
      filter={() => true}
      data={options}
      placeholder='Select coach role'
      {...autocompleteProps}
    />
  );
};

export default AppointmentCoachRoleAutoComplete;
